<template>
  <BaseModal
    :heading="$t('global.actions.add_customer')"
    small
    useForm
    @submit="onSubmit"
    @close="emit('close')"
  >
    <Flex direction="column">
      <CustomerSearch
        @select="addCustomer"
        v-test="'customer-search'"
        :focus="true"
      />
      <CustomerDetails
        v-if="customer"
        :customer="customer"
        :options="['remove']"
        @select="customer = null"
        v-test="'customer-preview'"
      />
      <BaseText
        v-if="!customer && !showCreateCustomerForm"
        @click="showCreateCustomerForm = true"
        v-test="'customer-new'"
      >
        {{ $t('appointment.create.add_new_customer') }}
      </BaseText>
      <CreateCustomer
        v-if="showCreateCustomerForm"
        :customer="newCustomer"
        :requireAddress="requireAddress"
        :requireMedicalData="requireMedicalData"
      />
    </Flex>
    <template #footer>
      <BaseButton
        submitForm
        :loading="loading"
        :disabled="!isSubmittable"
        v-test="'customer-submit'"
      >
        {{ showCreateCustomerForm ? (ctaTextCreate || $t('global.actions.save')) : (ctaTextDefault || $t('global.actions.save')) }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  lang="ts"
  setup
>
import { useMutation } from '@vue/apollo-composable';
import CustomerDetails from '@/components/customer/customer-details/index.vue';
import CustomerSearch from '@/components/customer-search/index.vue';
import type { Customer } from '@/types';
import CreateCustomer from './CreateCustomerForm.vue';
import { CREATE_CUSTOMER } from './graphql';

withDefaults(
  defineProps<{
    ctaTextDefault?: string;
    ctaTextCreate?: string;
    requireAddress?: boolean;
    requireMedicalData?: boolean;
  }>(),
  {
    requireAddress: false,
    requireMedicalData: false,
  },
);

const emit = defineEmits<{
  submit: [customer: Customer];
  close: [];
}>();

const customer = ref<any>(null);
const showCreateCustomerForm = ref(false);

const addCustomer = (selectedCustomer: any) => {
  customer.value = selectedCustomer;
};

const newCustomer = ref<any>({
  firstName: '',
  lastName: '',
  email: '',
  mobilePhone: '',
});

const isSubmittable = computed(
  () => showCreateCustomerForm.value || !!customer.value,
);

const { mutate, loading } = useMutation(CREATE_CUSTOMER);

const onSubmit = () => {
  if (!isSubmittable.value) {
    return;
  }

  if (showCreateCustomerForm.value) {
    mutate({
      input: {
        customerAttributes: newCustomer.value,
      },
    }).then(
      ({
        data: {
          createCustomer: { customer },
        },
      }) => {
        emit('submit', customer);
        emit('close');
      },
    );
  } else {
    emit('submit', customer.value);
    emit('close');
  }
};

watch(customer, () => {
  showCreateCustomerForm.value = false;
});
</script>
